import { useState, useEffect, useRef } from 'react';
import { useSearch, useNavigate } from '@tanstack/react-router';
import { Card } from 'src/components/ui/card';
import SalesOrderDraftsTable from './components/sales-order-drafts-table/SalesOrderDraftsTable';
import SalesOrderFilters, {
  FilterValues,
} from './components/SalesOrderFilters';
import Page from 'src/components/page/Page';
import {
  loadFiltersFromCookie,
  saveFiltersToCookie,
  ExtendedFilterValues,
} from './utils/salesOrderDraftsCookies';
import { ForegroundAgentChat } from '@/components/common/ForegroundAgentChat';
import { SalesOrderDraftStatus } from 'src/types/application/SalesOrderDraftDTO';
import { useAgentState } from '@/context/agent-state.context';
import { useAgentPanel } from '@/context/agent-panel.context';

interface SalesOrdersSearchParams {
  salesOrderId?: string;
  isReadyForApproval?: 'true' | 'false';
  status?: string;
}

const SalesOrderDraftsPage: React.FC = () => {
  const searchParams = useSearch({
    from: '/authenticated/sales-orders',
  }) as SalesOrdersSearchParams;
  const navigate = useNavigate();
  const hasAppliedUrlParams = useRef(false);
  const { reset: resetAgentChat } = useAgentState();
  const { close: closeAgentPanel } = useAgentPanel();

  // Reset and close the AI agent chat when entering/leaving this page
  useEffect(() => {
    resetAgentChat();
    closeAgentPanel();

    return () => {
      resetAgentChat();
      closeAgentPanel();
    };
  }, [resetAgentChat, closeAgentPanel]);

  // Initialize filters from URL params or cookie
  const [filters, setFilters] = useState<ExtendedFilterValues>(() => {
    // Check for URL params first
    if (searchParams.isReadyForApproval || searchParams.status) {
      hasAppliedUrlParams.current = true;
      const urlFilters: ExtendedFilterValues = {};

      // Apply status filter from URL
      if (searchParams.status) {
        const validStatuses = searchParams.status
          .split(',')
          .filter((s) =>
            Object.values(SalesOrderDraftStatus).includes(
              s as SalesOrderDraftStatus,
            ),
          ) as SalesOrderDraftStatus[];
        if (validStatuses.length) {
          urlFilters.statuses = validStatuses;
        }
      }

      return urlFilters;
    }

    // Fall back to cookie filters
    return loadFiltersFromCookie();
  });

  // Track isReadyForApproval from URL separately (not a standard filter)
  const [isReadyForApprovalFilter, setIsReadyForApprovalFilter] = useState<
    boolean | undefined
  >(() => {
    if (searchParams.isReadyForApproval === 'true') return true;
    if (searchParams.isReadyForApproval === 'false') return false;
    return undefined;
  });

  // Clear URL params after applying them (to prevent stale state on refresh)
  useEffect(() => {
    if (
      hasAppliedUrlParams.current &&
      (searchParams.isReadyForApproval || searchParams.status)
    ) {
      // Clear URL params after a short delay to allow the filters to apply
      const timer = setTimeout(() => {
        navigate({
          to: '/sales-orders',
          search: { salesOrderId: searchParams.salesOrderId },
          replace: true,
        });
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [
    navigate,
    searchParams.isReadyForApproval,
    searchParams.status,
    searchParams.salesOrderId,
  ]);

  // Save filters to cookie whenever they change (but not for URL-param-based filters)
  useEffect(() => {
    // Only save to cookie if not using URL param filters
    if (!hasAppliedUrlParams.current) {
      saveFiltersToCookie(filters);
    }
  }, [filters]);

  const handleFiltersChange = (newFilters: FilterValues) => {
    // When user changes filters, clear the URL param filters
    hasAppliedUrlParams.current = false;
    setIsReadyForApprovalFilter(undefined);
    setFilters(newFilters);
  };

  return (
    <Page title="Sales Orders">
      <Card className="mb-3 mt-3">
        <SalesOrderFilters
          filters={filters}
          onFiltersChange={handleFiltersChange}
        />
      </Card>

      <Card>
        <SalesOrderDraftsTable
          filters={filters}
          isReadyForApproval={isReadyForApprovalFilter}
        />
      </Card>

      <ForegroundAgentChat
        moduleKey="sales-orders"
        currentPage="/sales-orders"
      />
    </Page>
  );
};

export default SalesOrderDraftsPage;
